import apiClient from './http';
import type { KmsRequest, KmsSaveResult } from '../entities/Kms';
import type { RealtimeConfigCategory } from '../entities/RealtimeConfig';

const API_PREFIX = '/service/kms';

function baseRequest(appKey: string, category: RealtimeConfigCategory): KmsRequest {
    return {
        appKey,
        category,
    };
}

export class KmsService {
    static async save(appKey: string, category: RealtimeConfigCategory, key: string, value: string): Promise<KmsSaveResult> {
        const response = await apiClient.post(`${API_PREFIX}/insert`, {
            ...baseRequest(appKey, category),
            key,
            value,
        });
        return response.data;
    }

    static async saveWithToken(appKey: string, category: RealtimeConfigCategory, value: string): Promise<KmsSaveResult> {
        const response = await apiClient.post(`${API_PREFIX}/insert`, {
            ...baseRequest(appKey, category),
            value,
            useToken: true,
        });
        return response.data;
    }

    static async updateWithToken(appKey: string, category: RealtimeConfigCategory, token: string, value: string): Promise<KmsSaveResult> {
        const response = await apiClient.post(`${API_PREFIX}/update`, {
            ...baseRequest(appKey, category),
            key: token,
            value,
            useToken: true,
        });
        return response.data;
    }

    static async getValue(appKey: string, category: RealtimeConfigCategory, key: string): Promise<string | null> {
        const response = await apiClient.post(`${API_PREFIX}/value/get`, {
            ...baseRequest(appKey, category),
            key,
        });
        return response.data ?? null;
    }

    static async selectTokenByValue(appKey: string, category: RealtimeConfigCategory, value: string): Promise<string | null> {
        const response = await apiClient.post(`${API_PREFIX}/token/get`, {
            ...baseRequest(appKey, category),
            value,
        });
        return response.data ?? null;
    }

    static async deleteByKey(appKey: string, category: RealtimeConfigCategory, key: string): Promise<void> {
        await apiClient.post(`${API_PREFIX}/delete`, {
            ...baseRequest(appKey, category),
            key,
        });
    }

    static async deleteByToken(appKey: string, category: RealtimeConfigCategory, token: string): Promise<void> {
        await apiClient.post(`${API_PREFIX}/delete`, {
            ...baseRequest(appKey, category),
            key: token,
            useToken: true,
        });
    }

    static async generateRsaPublicKey(appKey: string): Promise<string> {
        const response = await apiClient.post(`${API_PREFIX}/rsa/publicKey/generate`, {
            appKey,
            category: 'GENERAL',
        });
        return response.data;
    }

    static async decryptByRsaPublicKey(appKey: string, publicKey: string, encryptedValue: string): Promise<string | null> {
        const response = await apiClient.post(`${API_PREFIX}/rsa/decrypt`, {
            appKey,
            category: 'GENERAL',
            key: publicKey,
            value: encryptedValue,
        });
        return response.data ?? null;
    }
}
